import React, { useRef, useEffect, useState } from 'react';
import Hls from 'hls.js';
import { Card, Spinner, Alert } from 'react-bootstrap';
import { baseURL } from '../axios';

export default function CPPlusLive({
  title = "Live Camera Feed",
  showControls = true,
  cameraData = null,
  checkOtherOptions = true,
  streamUrl = `${baseURL}/streams/cam_${cameraData?.id}.m3u8`
}) {
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [displayTime, setDisplayTime] = useState(new Date().toLocaleTimeString());

  useEffect(() => {
    const video = videoRef.current;
    const hls = new Hls();

    if (Hls.isSupported()) {
      hls.loadSource(streamUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setLoading(false);
        video
          .play()
          .then(() => {
            setIsPlaying(true);
            setIsMuted(video.muted);
          })
          .catch(err => {
            setError('Auto-play failed. Please click play manually.');
            setIsPlaying(false);
          });
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        setLoading(false);
        setError('Failed to load video stream');
      });

    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = streamUrl;
      video.addEventListener('loadedmetadata', () => {
        setLoading(false);
        video
          .play()
          .then(() => {
            setIsPlaying(true);
            setIsMuted(video.muted);
          })
          .catch(err => {
            setError('Auto-play failed. Please click play manually.');
            setIsPlaying(false);
          });
      });
      video.addEventListener('error', () => {
        setLoading(false);
        setError('Failed to load video stream');
      });
    } else {
      setLoading(false);
      setError('Your browser does not support HLS video streaming');
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [streamUrl]);

  // Update on-screen time only while playing
  useEffect(() => {
    if (!isPlaying) {
      return;
    }

    const intervalId = setInterval(() => {
      setDisplayTime(new Date().toLocaleTimeString());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isPlaying]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      videoRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video
        .play()
        .then(() => {
          setIsPlaying(true);
          setIsMuted(video.muted);
        })
        .catch(() => {
          // ignore play error here, error state already handled in loader
        });
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleToggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const changeVolume = delta => {
    const video = videoRef.current;
    if (!video) return;

    const current = Number.isFinite(video.volume) ? video.volume : 1;
    let next = current + delta;
    if (next < 0) next = 0;
    if (next > 1) next = 1;

    video.volume = next;
    if (next > 0 && video.muted) {
      video.muted = false;
    }

    setIsMuted(video.muted);
  };

  return (
    <Card className="border-0 shadow-sm overflow-hidden">
      
      {checkOtherOptions && (
        <Card.Header className="bg-dark text-white py-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div className="me-3">
                <div className="bg-danger rounded-circle d-inline-block" style={{ width: '8px', height: '8px' }}>
                  <div className="bg-danger rounded-circle animate-pulse" style={{ width: '8px', height: '8px' }}></div>
                </div>
              </div>
              <h5 className="mb-0">
                <i className="mdi mdi-video me-2"></i>
                {cameraData?.name || title}
              </h5>
            </div>
            {showControls && (
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-light"
                  onClick={toggleFullscreen}
                  title="Toggle Fullscreen"
                >
                  <i className={`mdi ${isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen'}`}></i>
                </button>
              </div>
            )}
          </div>
        </Card.Header>
        )}
      
      <Card.Body className="p-0 position-relative">
        {loading && (
          <div className="position-absolute top-50 start-50 translate-middle text-center">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2 text-muted">Loading video stream...</div>
          </div>
        )}
        {checkOtherOptions && (
          <>
          {error && (
            <Alert variant="danger" className="m-3">
              <i className="mdi mdi-alert-circle me-2"></i>
              {error}
            </Alert>
          )}
          </>
        )}
        

        <div className="video-container position-relative">
          <video
            ref={videoRef}
            controls={false}
            muted={isMuted}
            className="w-100"
            style={{
              minHeight: '300px',
              backgroundColor: '#000',
              objectFit: 'contain'
            }}
            poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23000'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23fff' font-size='16'%3ELoading...%3C/text%3E%3C/svg%3E"
          />

          {/* Top overlay info (LIVE badge + feed name + time) */}
          <div className="position-absolute bottom-0 start-0 end-0 bg-gradient-dark p-3" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
            <div className="d-flex align-items-center justify-content-between text-white">
              <div className="d-flex align-items-center">
                <span className="badge bg-danger me-2">LIVE</span>
                <small>{cameraData?.name || 'Camera 1'} - {cameraData?.nvrIdCameraCameraNvr?.name || 'Main Feed'}</small>
              </div>
              <div className="d-flex align-items-center">
                <small>{displayTime}</small>
              </div>
            </div>
          </div>
        </div>

        {showControls && (
          <div className="d-flex align-items-center justify-content-center gap-2 py-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handlePlayPause}
            >
              <i className={`mdi ${isPlaying ? 'mdi-pause' : 'mdi-play'}`}></i>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleToggleMute}
            >
              <i className={`mdi ${isMuted ? 'mdi-volume-off' : 'mdi-volume-high'}`}></i>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => changeVolume(-0.1)}
            >
              <i className="mdi mdi-volume-minus"></i>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => changeVolume(0.1)}
            >
              <i className="mdi mdi-volume-plus"></i>
            </button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
